import {
  ArdoqId,
  BroadcastAggregate,
  ReportRuleOperator,
} from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';

const ENGLISH_REPORT_RULE_OPERATOR_AS_TEXT: Record<ReportRuleOperator, string> =
  {
    [ReportRuleOperator.GREATER]: 'greater than',
    [ReportRuleOperator.LESS]: 'less than',
    [ReportRuleOperator.EQUAL]: 'equal to',
    [ReportRuleOperator.NOT_EQUAL]: 'not equal to',
  };

const ENGLISH_BROADCAST_AGGREGATE_LABEL: Record<BroadcastAggregate, string> = {
  [BroadcastAggregate.MIN]: 'Minimum',
  [BroadcastAggregate.MAX]: 'Maximum',
  [BroadcastAggregate.AVERAGE]: 'Average (including not set values)',
  [BroadcastAggregate.AVERAGE_SET]: 'Average (only set values)',
  [BroadcastAggregate.SUM]: 'Sum',
  [BroadcastAggregate.COUNT]: 'Count (all)',
  [BroadcastAggregate.COUNT_NOT_SET]: 'Count (only not set values)',
  [BroadcastAggregate.COUNT_SET]: 'Count (only set values)',
  [BroadcastAggregate.MEDIAN]: 'Median',
};

const ENGLISH_REPORT_RULE_OPERATOR_LABEL: Record<ReportRuleOperator, string> = {
  [ReportRuleOperator.GREATER]: 'Is higher than',
  [ReportRuleOperator.LESS]: 'Is lower than',
  [ReportRuleOperator.EQUAL]: 'Equal to',
  [ReportRuleOperator.NOT_EQUAL]: 'Not equal',
};

const BROADCAST_AGGREGATE_KEY: Record<BroadcastAggregate, string> = {
  [BroadcastAggregate.MIN]:
    'broadcasts:builder.content.reportRules.aggregates.minimum',
  [BroadcastAggregate.MAX]:
    'broadcasts:builder.content.reportRules.aggregates.maximum',
  [BroadcastAggregate.AVERAGE]:
    'broadcasts:builder.content.reportRules.aggregates.averageIncludingNotSet',
  [BroadcastAggregate.AVERAGE_SET]:
    'broadcasts:builder.content.reportRules.aggregates.averageOnlySet',
  [BroadcastAggregate.SUM]:
    'broadcasts:builder.content.reportRules.aggregates.sum',
  [BroadcastAggregate.COUNT]:
    'broadcasts:builder.content.reportRules.aggregates.countAll',
  [BroadcastAggregate.COUNT_NOT_SET]:
    'broadcasts:builder.content.reportRules.aggregates.countNotSet',
  [BroadcastAggregate.COUNT_SET]:
    'broadcasts:builder.content.reportRules.aggregates.countSet',
  [BroadcastAggregate.MEDIAN]:
    'broadcasts:builder.content.reportRules.aggregates.median',
};

const REPORT_RULE_OPERATOR_KEY: Record<ReportRuleOperator, string> = {
  [ReportRuleOperator.GREATER]:
    'broadcasts:builder.content.reportRules.operators.greaterThan',
  [ReportRuleOperator.LESS]:
    'broadcasts:builder.content.reportRules.operators.lessThan',
  [ReportRuleOperator.EQUAL]:
    'broadcasts:builder.content.reportRules.operators.equalTo',
  [ReportRuleOperator.NOT_EQUAL]:
    'broadcasts:builder.content.reportRules.operators.notEqual',
};

const REPORT_RULE_OPERATOR_AS_TEXT_KEY: Record<ReportRuleOperator, string> = {
  [ReportRuleOperator.GREATER]:
    'broadcasts:builder.content.reportRules.operatorsAsText.greaterThan',
  [ReportRuleOperator.LESS]:
    'broadcasts:builder.content.reportRules.operatorsAsText.lessThan',
  [ReportRuleOperator.EQUAL]:
    'broadcasts:builder.content.reportRules.operatorsAsText.equalTo',
  [ReportRuleOperator.NOT_EQUAL]:
    'broadcasts:builder.content.reportRules.operatorsAsText.notEqual',
};

export const getBroadcastAggregateLabel = (
  aggregate: BroadcastAggregate,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    BROADCAST_AGGREGATE_KEY[aggregate],
    ENGLISH_BROADCAST_AGGREGATE_LABEL[aggregate]
  );

export const getReportRuleOperatorLabel = (
  operator: ReportRuleOperator,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    REPORT_RULE_OPERATOR_KEY[operator],
    ENGLISH_REPORT_RULE_OPERATOR_LABEL[operator]
  );

export const getReportRuleOperatorAsText = (
  operator: ReportRuleOperator,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    REPORT_RULE_OPERATOR_AS_TEXT_KEY[operator],
    ENGLISH_REPORT_RULE_OPERATOR_AS_TEXT[operator]
  );

export const getBroadcastAggregateOptions = (
  i18nLocale: I18nLocale
): { value: BroadcastAggregate; label: string }[] =>
  (Object.keys(ENGLISH_BROADCAST_AGGREGATE_LABEL) as BroadcastAggregate[]).map(
    aggregate => ({
      value: aggregate,
      label: getBroadcastAggregateLabel(aggregate, i18nLocale),
    })
  );

export const getReportRuleOperatorOptions = (
  i18nLocale: I18nLocale
): { value: ReportRuleOperator; label: string }[] =>
  (Object.keys(ENGLISH_REPORT_RULE_OPERATOR_LABEL) as ReportRuleOperator[]).map(
    operator => ({
      value: operator,
      label: getReportRuleOperatorLabel(operator, i18nLocale),
    })
  );

export type ReportRulesSection$State = {
  aggregate: BroadcastAggregate | null;
  columnName: ArdoqId | null;
  predicate: ReportRuleOperator | null;
  threshold: number | null;
};
